import { lazy as reactLazy, type ComponentType } from "react";

/**
 * Wraps `React.lazy` with chunk-load-error recovery.
 *
 * Why this exists: when a deploy ships and a user has a stale tab open, the
 * code-split chunk filenames they're holding are no longer on the server.
 * The next dynamic import (a route navigation, a lazy-mounted dialog, etc.)
 * throws one of:
 *   - `TypeError: Failed to fetch dynamically imported module`
 *   - `TypeError: Importing a module script failed`
 *   - `ChunkLoadError`
 *
 * Sentry top issues SHOPLEDGER-4, -5, -7, -9 are all this pattern. They're
 * not real bugs — they're an artifact of stale-tab-meets-new-deploy. The
 * standard fix is: retry once (in case it was a transient network blip),
 * then trigger a hard page reload to pick up the latest index.html with
 * the current chunk hashes.
 *
 * To avoid reload loops (if the chunk is genuinely gone server-side), we
 * gate reloads behind a sessionStorage cooldown.
 *
 * Usage: drop-in replacement for React.lazy. In App.tsx, change
 *     import { lazy } from "react";
 * to
 *     import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
 * and existing `lazy(() => import("./pages/Foo"))` calls keep working.
 */

const RELOAD_KEY = "__lazyChunkReloadAt";
const RELOAD_COOLDOWN_MS = 30_000;
const RETRY_DELAY_MS = 200;

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { message?: string; name?: string };
  const msg = (e.message ?? String(err)).toLowerCase();
  return (
    e.name === "ChunkLoadError" ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    (msg.includes("failed to load") && msg.includes("module"))
  );
}

function reloadOnceWithCooldown(): void {
  try {
    const last = parseInt(sessionStorage.getItem(RELOAD_KEY) ?? "0", 10);
    const now = Date.now();
    if (now - last < RELOAD_COOLDOWN_MS) {
      // Already reloaded recently — don't loop.
      return;
    }
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // sessionStorage unavailable (private mode, sandboxed iframe). Reload anyway.
  }
  window.location.reload();
}

// Match React.lazy's signature exactly — `ComponentType<any>` is what the
// upstream type uses, and using `unknown` here would refuse pages that have
// strictly-typed props (the contravariance bites: `ComponentType<FooProps>`
// is not assignable to `ComponentType<unknown>`).
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof reactLazy<T>> {
  return reactLazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;

      // First failure — retry once after a short delay. Transient network
      // blips and brief CDN propagation gaps resolve here.
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      try {
        return await factory();
      } catch (err2) {
        if (!isChunkLoadError(err2)) throw err2;

        // Second failure — the chunk hash is genuinely stale. Trigger a
        // hard reload to fetch the new index.html with current hashes.
        reloadOnceWithCooldown();

        // Re-throw so the surrounding <ErrorBoundary> catches and shows the
        // fallback UI while the reload is in flight.
        throw err2;
      }
    }
  });
}
